import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Loader2, ExternalLink, Copy, RefreshCw, Power, ShieldCheck, Bug, Play } from "lucide-react";

// iter100 — Admin settings card for Thumbtack webhook integration:
//   - Show masked secret + configured state
//   - Enable / disable webhook processing at runtime
//   - Rotate the shared secret (returns plaintext once)
//   - Copy the production webhook URL
//   - View the last N webhook payloads for debugging
export default function ThumbtackWebhookPanel() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ configured: false, enabled: true,
                                        masked: "", source: "env" });
  const [rotating, setRotating] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [simMode, setSimMode] = useState("signature");
  const [simRunning, setSimRunning] = useState(false);
  const [simResult, setSimResult] = useState(null);

  const webhookUrl = `${window.location.origin}/api/webhooks/thumbtack`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/thumbtack/current-secret");
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Thumbtack settings");
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get(
        `/admin/thumbtack/webhook-logs?limit=25${onlyFailed ? "&only_failed=1" : ""}`,
      );
      setLogs(data.logs || []);
    } catch (e) {
      toast.error("Could not load webhook logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const loadDiag = async () => {
    setDiagLoading(true);
    try {
      const { data } = await api.get("/admin/thumbtack/last-webhook-diagnostic");
      setDiag(data);
    } catch (e) {
      toast.error("Could not load diagnostic");
    } finally {
      setDiagLoading(false);
    }
  };

  const runSimulator = async () => {
    setSimRunning(true);
    setSimResult(null);
    try {
      const { data } = await api.post("/admin/thumbtack/simulate-webhook", {
        event_type: "new_lead",
        auth_mode: simMode,
      });
      setSimResult(data);
      if (data.ok && data.log?.auth_ok) {
        toast.success(`Simulator: auth OK · method=${data.log.auth_method_used}`);
      } else {
        toast.error(`Simulator: auth failed · ${data.log?.auth_reason || "unknown"}`);
      }
      loadLogs();
      loadDiag();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulator failed");
    } finally {
      setSimRunning(false);
    }
  };

  useEffect(() => { load(); loadLogs(); loadDiag(); }, []);
  useEffect(() => { loadLogs(); }, [onlyFailed]);

  const toggle = async (nextEnabled) => {
    setToggling(true);
    try {
      await api.post("/admin/thumbtack/settings", { enabled: nextEnabled });
      toast.success(`Thumbtack webhooks ${nextEnabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update setting");
    } finally {
      setToggling(false);
    }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the Thumbtack webhook secret? You'll need to paste the new value into Thumbtack's partner console + deployment env vars.")) return;
    setRotating(true);
    setNewSecret(null);
    try {
      const { data } = await api.post("/admin/thumbtack/rotate-secret", {});
      setNewSecret(data.new_secret);
      toast.success("New secret generated. Copy it now — it won't be shown again.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotate failed");
    } finally {
      setRotating(false);
    }
  };

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 max-w-3xl" data-testid="thumbtack-webhook-panel">
      <div className="flex items-center gap-2">
        <ExternalLink className="w-5 h-5 text-cyan-700" />
        <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Integration</div>
      </div>
      <h2 className="mt-1 text-xl font-extrabold font-[Outfit] tracking-tighter text-gray-900">Thumbtack Webhooks</h2>
      <p className="mt-1 text-sm text-gray-600">
        New leads, messages, reviews, and job status updates from Thumbtack
        are automatically imported into your CRM. Turn processing on or
        off at any time — even when off, payloads are still logged for
        debugging so no data is lost.
      </p>

      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="mt-5 space-y-4">
          {/* Webhook URL */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Production webhook URL</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <code className="flex-1 min-w-0 truncate bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-[12px] font-mono text-gray-900" data-testid="thumbtack-webhook-url">
                {webhookUrl}
              </code>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => copy(webhookUrl)} data-testid="thumbtack-webhook-copy">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-gray-500">Paste this into Thumbtack&apos;s Partner Console → Webhooks. Accepts POST JSON.</p>
          </div>

          {/* Enable / Disable toggle */}
          <div className="rounded-2xl border border-gray-200 p-4 flex items-start gap-3 flex-wrap">
            <Power className={`w-5 h-5 mt-0.5 ${state.enabled ? "text-emerald-600" : "text-gray-400"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-extrabold text-gray-900">
                {state.enabled ? "Webhook processing ENABLED" : "Webhook processing DISABLED"}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {state.enabled
                  ? "Incoming events create leads, customers, and bookings. SMS + email notifications fire."
                  : "Incoming events are received + logged, but NO leads/customers/bookings are created."}
              </div>
            </div>
            <Button
              size="sm"
              className={`rounded-full font-extrabold ${state.enabled ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
              onClick={() => toggle(!state.enabled)}
              disabled={toggling}
              data-testid="thumbtack-toggle-enabled"
            >
              {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : (state.enabled ? "Disable" : "Enable")}
            </Button>
          </div>

          {/* Secret + rotation */}
          <div className="rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <ShieldCheck className="w-5 h-5 text-gray-600" />
              <div className="text-[14px] font-extrabold text-gray-900">Shared secret</div>
              {state.configured ? (
                <span className="text-[9px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300">
                  Configured
                </span>
              ) : (
                <span className="text-[9px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300">
                  Not configured
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <code className="flex-1 min-w-0 truncate bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-[12px] font-mono text-gray-700" data-testid="thumbtack-secret-masked">
                {state.masked || "—"}
              </code>
              <Button size="sm" variant="outline" className="rounded-full" onClick={rotate} disabled={rotating} data-testid="thumbtack-secret-rotate">
                {rotating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Rotate</>}
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-gray-500">
              Source: <b>{state.source}</b>{state.rotated_at ? ` · Last rotated ${state.rotated_at.slice(0,10)} by ${state.rotated_by || "admin"}` : ""}.
              Thumbtack signs webhooks with this secret (HMAC-SHA256) — we also accept it as a bearer token in `X-Thumbtack-Secret` for partners not yet issuing signatures.
            </p>
            {newSecret && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-300 p-3">
                <div className="text-[11px] uppercase tracking-[0.16em] font-extrabold text-amber-800 mb-1">Copy this — shown once only</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 min-w-0 truncate text-[12px] font-mono text-gray-900">{newSecret}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(newSecret)}><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            )}
          </div>

          {/* iter114 — Diagnostics: last webhook attempt */}
          <div className="rounded-2xl border border-gray-200 p-4" data-testid="thumbtack-diagnostic-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-indigo-600" />
                <div className="text-[14px] font-extrabold text-gray-900">Last webhook diagnostic</div>
              </div>
              <Button size="sm" variant="outline" className="rounded-full" onClick={loadDiag} disabled={diagLoading} data-testid="thumbtack-diag-refresh">
                {diagLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </Button>
            </div>
            {!diag?.has_log ? (
              <div className="mt-2 text-[12px] text-gray-500" data-testid="thumbtack-diag-empty">
                No webhook received yet. Configure Thumbtack to POST to the URL above, or run the simulator below.
              </div>
            ) : (
              <div className="mt-2 space-y-2 text-[11px]">
                <div className="grid grid-cols-2 gap-2">
                  <div><b>Event:</b> <span className="font-mono">{diag.log.event_type}</span></div>
                  <div><b>Received:</b> <span className="font-mono">{diag.log.received_at?.replace("T"," ").slice(0,19)}</span></div>
                  <div><b>Auth:</b> <span className={diag.log.auth_ok ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"} data-testid="thumbtack-diag-auth-ok">
                    {diag.log.auth_ok ? "OK" : "FAILED"}
                  </span></div>
                  <div><b>Method:</b> <span className="font-mono">{diag.log.auth_method_used || "-"}</span></div>
                  <div><b>HTTP:</b> <span className="font-mono">{diag.log.http_status}</span></div>
                  <div><b>Body bytes:</b> <span className="font-mono">{diag.log.raw_body_len}</span></div>
                </div>
                <div className="p-2 rounded bg-gray-50 border border-gray-200">
                  <b>Reason:</b> <span className="font-mono break-all" data-testid="thumbtack-diag-reason">{diag.log.auth_reason}</span>
                </div>
                <details className="rounded bg-slate-50 border border-slate-200 p-2">
                  <summary className="cursor-pointer font-bold text-slate-700">Signature check</summary>
                  <pre className="mt-1 whitespace-pre-wrap text-[10px] font-mono">{JSON.stringify(diag.log.auth_diagnostic?.signature_check, null, 2)}</pre>
                </details>
                <details className="rounded bg-slate-50 border border-slate-200 p-2">
                  <summary className="cursor-pointer font-bold text-slate-700">Bearer check</summary>
                  <pre className="mt-1 whitespace-pre-wrap text-[10px] font-mono">{JSON.stringify(diag.log.auth_diagnostic?.bearer_check, null, 2)}</pre>
                </details>
                <details className="rounded bg-slate-50 border border-slate-200 p-2">
                  <summary className="cursor-pointer font-bold text-slate-700">Detected auth headers</summary>
                  <pre className="mt-1 whitespace-pre-wrap text-[10px] font-mono">{JSON.stringify(diag.log.auth_diagnostic?.detected_auth_headers, null, 2)}</pre>
                </details>
                <details className="rounded bg-slate-50 border border-slate-200 p-2">
                  <summary className="cursor-pointer font-bold text-slate-700">All received headers (redacted)</summary>
                  <pre className="mt-1 whitespace-pre-wrap text-[10px] font-mono">{JSON.stringify(diag.log.headers, null, 2)}</pre>
                </details>
                <details className="rounded bg-slate-50 border border-slate-200 p-2">
                  <summary className="cursor-pointer font-bold text-slate-700">Secret diagnostic (this server)</summary>
                  <pre className="mt-1 whitespace-pre-wrap text-[10px] font-mono">{JSON.stringify(diag.log.auth_diagnostic?.secret_diag, null, 2)}</pre>
                </details>
              </div>
            )}
          </div>

          {/* iter114 — Simulator */}
          <div className="rounded-2xl border border-gray-200 p-4" data-testid="thumbtack-simulator-panel">
            <div className="flex items-center gap-2">
              <Play className="w-5 h-5 text-fuchsia-600" />
              <div className="text-[14px] font-extrabold text-gray-900">Test the webhook pipeline</div>
            </div>
            <p className="mt-1 text-[11px] text-gray-500">
              Locally simulate an incoming Thumbtack event to verify signature + bearer auth before pointing Thumbtack at this server. No external network call.
            </p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <select
                className="text-xs rounded-full border border-gray-300 px-3 py-1.5 bg-white"
                value={simMode}
                onChange={(e) => setSimMode(e.target.value)}
                data-testid="thumbtack-sim-mode"
              >
                <option value="signature">Signature (correct HMAC)</option>
                <option value="bearer">Bearer (X-Thumbtack-Secret)</option>
                <option value="wrong_secret">Wrong signature (should FAIL)</option>
                <option value="none">No auth headers (should FAIL)</option>
              </select>
              <Button size="sm" className="rounded-full bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
                       onClick={runSimulator} disabled={simRunning || !state.configured}
                       data-testid="thumbtack-sim-run">
                {simRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run simulator"}
              </Button>
            </div>
            {simResult && (
              <div className={`mt-2 rounded-lg p-2 border text-[11px] ${simResult.log?.auth_ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
                    data-testid="thumbtack-sim-result">
                <div className="font-bold">
                  {simResult.log?.auth_ok ? "AUTH OK" : "AUTH FAILED"} · HTTP {simResult.http_status}
                </div>
                <div>Method: <span className="font-mono">{simResult.log?.auth_method_used || "-"}</span></div>
                <div>Reason: <span className="font-mono break-all">{simResult.log?.auth_reason}</span></div>
              </div>
            )}
          </div>

          {/* Recent webhook logs */}
          <div className="rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[14px] font-extrabold text-gray-900">Recent webhook events (last 25)</div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-[11px] text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)}
                          className="w-3.5 h-3.5 accent-rose-600" data-testid="thumbtack-logs-only-failed" />
                  Only failed auth
                </label>
                <Button size="sm" variant="outline" className="rounded-full" onClick={loadLogs} disabled={logsLoading} data-testid="thumbtack-logs-refresh">
                  {logsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
            {logs.length === 0 ? (
              <div className="mt-2 text-[12px] text-gray-500" data-testid="thumbtack-logs-empty">No events received yet.</div>
            ) : (
              <div className="mt-2 max-h-80 overflow-y-auto divide-y divide-gray-100" data-testid="thumbtack-logs-list">
                {logs.map((l) => (
                  <div key={l.log_id} className="py-2 flex items-start justify-between gap-3 text-[11px]">
                    <div className="min-w-0 flex-1">
                      <div className="font-extrabold text-gray-800 truncate">{l.event_type} · {l.received_at?.replace("T", " ").slice(0, 19)}</div>
                      <div className="text-gray-500 truncate">
                        auth={l.auth_reason || "-"} · processed={l.processed ? "yes" : "no"}
                        {l.error ? ` · ERR: ${l.error}` : ""}
                      </div>
                    </div>
                    <span className={`shrink-0 text-[9px] uppercase tracking-[0.14em] font-extrabold px-2 py-0.5 rounded-full border ${
                      l.processed ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                  : (l.error ? "bg-rose-50 text-rose-800 border-rose-200"
                                             : "bg-gray-50 text-gray-700 border-gray-200")
                    }`}>{l.processed ? "OK" : (l.error ? "ERR" : "SKIP")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
